/*
  Wishlist Hero, wired into the theme.

  The app's panel is its own React tree and the theme cannot reach inside it. What the
  theme can do is intercept the two buttons whose behaviour belongs to the theme rather
  than to the app: add-to-cart, and add-all-to-cart. Both of them navigate to /cart, and
  this theme does not navigate to /cart — it opens the bag over the page.

  HOW, AND WHY NOT BY REIMPLEMENTING IT

  assets/cart-drawer.js already owns adding to cart: a delegated submit handler on
  form[action*="/cart/add"] that posts to cart/add.js, re-renders the drawer's section,
  refreshes the badge and opens the bag — including the sold-out path. Rather than call
  cart/add.js again here and end up with two versions of that logic drifting apart, this
  file builds the form that handler is already waiting for and submits it. Everything
  after the submit is the theme's existing code.

  The click is taken in the capture phase and stopped there, because the app's own
  handler is what would otherwise send the shopper to /cart.

  WHERE THE VARIANT ID COMES FROM

  Off the card's product link (…/products/x?variant=123). The app also encodes it in the
  price node's id, but the link is the one it has to keep correct for the card to work at
  all, so it is the one less likely to change shape under an update.
*/

(function () {
  'use strict';

  var ADD_ONE = '.wishlist-hero-list-card-footer-add-to-cart';
  var ADD_ALL = '.wishlist-hero-list-card-footer-add-all-to-cart-dialog';
  var CARD = '.wishlist-hero-list-card';

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function fromUrl(value) {
    var match = /[?&]variant=(\d+)/.exec(value || '');
    return match ? match[1] : null;
  }

  /* Three places the app puts the variant id, tried in order of how load-bearing they
     are for the app itself — the product link has to stay correct or the card does not
     work at all, so it is the least likely to change shape under an update. The price
     node is the fallback because it carries the id twice, in its own id and in a data
     attribute. */
  function variantOf(card) {
    if (!card) return null;

    var link = card.querySelector('a[href*="variant="]');
    if (link) {
      var fromLink = fromUrl(link.getAttribute('href'));
      if (fromLink) return fromLink;
    }

    var price = card.querySelector('[id^="wishlist-hero-price-update-"]');
    if (price) {
      var fromId = /wishlist-hero-price-update-(\d+)/.exec(price.id);
      if (fromId) return fromId[1];

      var fromData = fromUrl(price.getAttribute('data-product-link'));
      if (fromData) return fromData;
    }

    var any = card.querySelector('[data-product-link]');
    if (any) return fromUrl(any.getAttribute('data-product-link'));

    return null;
  }

  /* One hidden form, submitted and removed. requestSubmit rather than submit, because
     submit() does not fire the submit event and the drawer's handler would never see it. */
  function addToCart(variants) {
    if (!variants.length) return false;

    var form = document.createElement('form');
    form.setAttribute('action', root() + 'cart/add');
    form.setAttribute('method', 'post');
    form.hidden = true;

    variants.forEach(function (id, index) {
      var field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'items[' + index + '][id]';
      field.value = id;
      form.appendChild(field);

      var quantity = document.createElement('input');
      quantity.type = 'hidden';
      quantity.name = 'items[' + index + '][quantity]';
      quantity.value = '1';
      form.appendChild(quantity);
    });

    document.body.appendChild(form);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      /* Older Safari. Dispatching the event by hand reaches the same delegated handler. */
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    form.remove();
    return true;
  }

  /* The bag opens over the page, so the wishlist panel underneath it would be a second
     overlay the shopper has to dismiss. Closing it is the app's own close button — the
     panel's open state lives in its React tree and clicking that button is the only way
     in from outside. */
  function closePanel() {
    var close = document.querySelector('.wishlist-hero-header-close');
    if (close) close.click();
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var one = target.closest(ADD_ONE);
      var all = one ? null : target.closest(ADD_ALL);
      if (!one && !all) return;

      var variants = [];

      if (one) {
        var variant = variantOf(one.closest(CARD));
        if (variant) variants.push(variant);
      } else {
        Array.prototype.forEach.call(document.querySelectorAll(CARD), function (card) {
          var id = variantOf(card);
          if (id && variants.indexOf(id) === -1) variants.push(id);
        });
      }

      /* No variant found means the app changed the markup all three readers above rely
         on. Its own handler is left to run and send the shopper to /cart — the wrong
         page, but not a dead button — and the reason is said out loud, because a silent
         fallback here is indistinguishable from this file never having loaded. */
      if (!variants.length) {
        if (window.console) {
          console.warn('[wishlist-hero] no variant id on the card; letting the app navigate to /cart');
        }
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (addToCart(variants)) closePanel();
    },
    true
  );
})();
